/**
 * SupportCampaignDetailPage — V9.19
 * Page publique d'une campagne SupportCampaign V1 (Athlete ou Club).
 * Affiche les mockups T-shirt officiels + formulaire de précommande.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { resolveAthletePhoto, resolveClubLogo, resolveFederationLogo } from "@/lib/photoFallback";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ShareButton } from "@/components/ShareButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Shirt,
  ArrowLeft,
  ShieldCheck,
  CheckCircle2,
  HandHeart,
  Info,
  Users,
  UserRound,
} from "lucide-react";

const SIZES = ["S", "M", "L", "XL", "XXL"];

function getVariantImage(campaign, color) {
  const useCustom =
    !campaign.use_default_template &&
    campaign.custom_visual_url &&
    campaign.custom_visual_approved;
  if (useCustom) return campaign.custom_visual_url;
  return color === "black"
    ? "/tshirt-templates/default_black_preview.png"
    : "/tshirt-templates/default_white_preview.png";
}

export default function SupportCampaignDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState("black");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone: "",
    size: "M",
    quantity: 1,
    consent_contact: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/support-campaigns/${slug}`);
      setC(data);
      const variants = data.tshirt_variants || ["black", "white"];
      setColor(variants[0]);
    } catch (e) {
      setC(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!c) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <Card className="p-8">
          <div className="text-lg font-display font-semibold mb-2">
            Campagne introuvable
          </div>
          <div className="text-sm text-muted-foreground mb-4">
            Cette campagne de soutien n'existe pas ou n'est plus active.
          </div>
          <Link to="/soutien/campagnes">
            <Button variant="outline" data-testid="support-campaign-back-list">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Retour aux campagnes
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const variants = c.tshirt_variants || ["black", "white"];
  const total = Number(c.total_preorders_all_tranches || 0);
  const THRESHOLD = 20;
  const pct = Math.min(100, Math.round((total / THRESHOLD) * 100));
  const thresholdReached = total >= THRESHOLD;
  const isActive =
    ["preorder_open", "threshold_reached", "confirmed_offline"].includes(
      c.status,
    ) && c.published;

  const price = Number(c.tshirt_price_xpf || 4500);
  const ben = c.beneficiary || {};
  const benName = ben.full_name || ben.name || "Bénéficiaire";
  // V11.2 — Résolution unifiée : vraie photo si présente, sinon fallback selon
  // sexe (Athlete) ou type (Club/Fédération). Le fallback n'est jamais stocké.
  const benPhoto =
    c.beneficiary_type === "club"
      ? resolveClubLogo(ben)
      : c.beneficiary_type === "federation"
      ? resolveFederationLogo(ben)
      : resolveAthletePhoto(ben);
  const ctaLabel =
    c.beneficiary_type === "club" ? "Soutenir ce club" : "Soutenir cet athlète";

  const submit = async () => {
    setBusy(true);
    try {
      const { data: rr } = await api.post(`/support-campaigns/${c.slug}/preorder`, {
        ...form,
        tshirt_color: color,
      });
      setSubmitted({ preorder_id: rr?.preorder_id, total: rr?.total_preorders });
      setOpen(false);
      toast.success("Merci ! Votre précommande est enregistrée.");
      load();
    } catch (e) {
      showError(e, { prefix: "Précommande :" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6" data-testid="support-campaign-detail">
      <Link to="/soutien/campagnes" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Toutes les campagnes
      </Link>

      {/* V9.22 — Hero visuel : Athlète/Club + T-shirt */}
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/10 to-[hsl(var(--aito-gold))]/5" data-testid="support-campaign-hero">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Colonne gauche : Photo bénéficiaire */}
          <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[280px] bg-muted/40 overflow-hidden">
            {benPhoto ? (
              <img
                src={benPhoto}
                alt={benName}
                className="w-full h-full object-cover"
                data-testid="support-campaign-hero-photo"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                {c.beneficiary_type === "club" ? (
                  <Users className="h-24 w-24 opacity-40" />
                ) : (
                  <UserRound className="h-24 w-24 opacity-40" />
                )}
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              <div className="text-white text-[10px] uppercase tracking-widest opacity-80">
                AITO.PF × {c.beneficiary_type === "club" ? "Club" : "Athlète"}
              </div>
              <div className="text-white font-display font-bold text-lg leading-tight" data-testid="support-campaign-hero-name">
                {benName}
              </div>
            </div>
          </div>
          {/* Colonne droite : Mockup T-shirt courant */}
          <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[280px] bg-[hsl(var(--muted))]/30 flex items-center justify-center">
            <img
              src={getVariantImage(c, color)}
              alt={`T-shirt ${color}`}
              className="w-full h-full object-contain p-4"
              data-testid="support-campaign-hero-tshirt"
            />
            <Badge className="absolute top-3 right-3 bg-black/70 text-white text-[10px] backdrop-blur-sm">
              T-shirt officiel AITO.PF
            </Badge>
          </div>
        </div>
      </Card>

      <div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="font-display text-2xl md:text-3xl font-bold" data-testid="support-campaign-title">
            {c.title}
          </h1>
          <ShareButton
            kind="campaign"
            slug={c.slug}
            title={`${c.title} — AITO.PF`}
            text={`Soutenez cette campagne AITO.PF — T-shirt en édition limitée.`}
            canonicalPath={`/soutien/${c.slug}`}
            size="sm"
            variant="outline"
            label="Partager"
            dataTestId="cta-share-support-campaign"
          />
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "Campagne active" : "Campagne clôturée"}
          </Badge>
          {c.objective && (
            <span className="text-xs text-muted-foreground">· {c.objective}</span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Visuel T-shirt */}
        <Card className="p-4 space-y-4" data-testid="support-campaign-visuals">
          <div className="flex items-center gap-2 flex-wrap">
            <Shirt className="h-4 w-4" />
            <span className="text-sm font-semibold">Choisis ta version</span>
            <div className="ml-auto flex items-center gap-1">
              {variants.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setColor(v)}
                  data-testid={`support-campaign-variant-${v}`}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    color === v
                      ? "bg-foreground text-background border-foreground"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  {v === "black" ? "Noir" : "Blanc"}
                </button>
              ))}
            </div>
          </div>
          <div className="aspect-[4/3] rounded-md overflow-hidden bg-muted/40 border">
            <img
              src={getVariantImage(c, color)}
              alt={`T-shirt ${color}`}
              className="w-full h-full object-contain"
              data-testid="support-campaign-mockup"
            />
          </div>
          <div className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" />
            Mockup officiel AITO.PF — recto + verso.
          </div>
        </Card>

        {/* Détails campagne + CTA */}
        <Card className="p-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Prix unitaire</div>
            <div className="text-2xl font-display font-bold" data-testid="support-campaign-price">
              {price.toLocaleString("fr-FR")} XPF
            </div>
          </div>
          {c.description && (
            <div className="text-sm text-muted-foreground whitespace-pre-line">
              {c.description}
            </div>
          )}
          {c.objective && (
            <div className="text-sm">
              <span className="font-semibold">Objectif : </span>
              {c.objective}
            </div>
          )}

          <div className="pt-2">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-xs font-semibold">Progression</div>
              <div className="text-xs text-muted-foreground ml-auto">
                {thresholdReached
                  ? `${total} précommande${total > 1 ? "s" : ""} enregistrée${total > 1 ? "s" : ""}`
                  : `${total} / ${THRESHOLD} précommande${total > 1 ? "s" : ""}`}
              </div>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--aito-lagoon))] transition-all"
                style={{ width: `${pct}%` }}
                data-testid="support-campaign-progress"
              />
            </div>
            {thresholdReached ? (
              <Badge className="mt-2 bg-emerald-600 text-white text-[10px]" data-testid="support-campaign-threshold-badge">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Seuil de production atteint · {THRESHOLD} précommandes
              </Badge>
            ) : (
              <div className="mt-1 text-[10px] text-muted-foreground">
                Production lancée à partir de {THRESHOLD} précommandes
              </div>
            )}
          </div>

          {isActive ? (
            <Button
              size="lg"
              className="w-full bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
              onClick={() => setOpen(true)}
              data-testid="support-campaign-preorder-btn"
            >
              <HandHeart className="h-4 w-4 mr-2" /> {ctaLabel}
            </Button>
          ) : (
            <div className="text-sm text-muted-foreground bg-muted rounded-md p-3">
              Les précommandes ne sont plus ouvertes pour cette campagne.
            </div>
          )}

          <div className="text-[11px] text-muted-foreground flex items-start gap-1">
            <ShieldCheck className="h-3 w-3 mt-0.5" />
            <span>
              Précommande sans paiement en ligne. AITO.PF vous contactera par
              e-mail pour finaliser hors plateforme.
            </span>
          </div>
        </Card>
      </div>

      {/* Dialog précommande */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="support-campaign-dialog">
          <DialogHeader>
            <DialogTitle>{ctaLabel}</DialogTitle>
            <DialogDescription>
              Précommande sans paiement en ligne. AITO.PF vous contactera pour finaliser.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="fn">Prénom</Label>
                <Input
                  id="fn"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="support-campaign-form-firstname"
                />
              </div>
              <div>
                <Label htmlFor="ln">Nom</Label>
                <Input
                  id="ln"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="support-campaign-form-lastname"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="em">Email</Label>
              <Input
                id="em"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="support-campaign-form-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="ph">Téléphone (optionnel)</Label>
                <Input
                  id="ph"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="support-campaign-form-phone"
                />
              </div>
              <div>
                <Label>Taille</Label>
                <Select
                  value={form.size}
                  onValueChange={(v) => setForm({ ...form, size: v })}
                >
                  <SelectTrigger data-testid="support-campaign-form-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Couleur</Label>
              <div className="mt-1 flex items-center gap-2">
                {variants.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setColor(v)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      color === v
                        ? "bg-foreground text-background border-foreground"
                        : "border-border hover:bg-muted"
                    }`}
                    data-testid={`support-campaign-form-color-${v}`}
                  >
                    {v === "black" ? "Noir" : "Blanc"}
                  </button>
                ))}
              </div>
            </div>
            {/* V11 Hotfix — Quantité */}
            <div>
              <Label className="text-xs">Quantité de T-shirts</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, quantity: Math.max(1, (form.quantity || 1) - 1) })} data-testid="support-qty-dec">-</Button>
                <span className="font-mono text-lg w-8 text-center" data-testid="support-qty-value">{form.quantity || 1}</span>
                <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, quantity: Math.min(20, (form.quantity || 1) + 1) })} data-testid="support-qty-inc">+</Button>
                {c?.tshirt_unit_price_xpf && (
                  <span className="ml-3 text-xs text-muted-foreground">
                    {form.quantity || 1} × <strong>{c.tshirt_unit_price_xpf.toLocaleString("fr-FR")}</strong> XPF = <strong className="text-foreground" data-testid="support-qty-total">{((form.quantity || 1) * c.tshirt_unit_price_xpf).toLocaleString("fr-FR")}</strong> XPF
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="cc"
                checked={form.consent_contact}
                onCheckedChange={(v) =>
                  setForm({ ...form, consent_contact: !!v })
                }
                data-testid="support-campaign-form-consent"
              />
              <Label htmlFor="cc" className="text-xs leading-relaxed">
                J'accepte d'être contacté(e) par AITO.PF pour finaliser ma
                précommande.
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="support-campaign-form-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={submit}
              disabled={busy || !form.first_name || !form.last_name || !form.email}
              data-testid="support-campaign-form-submit"
            >
              {busy ? "Envoi…" : "Valider ma précommande"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation */}
      {submitted && (
        <Card className="p-4 border-l-4 border-emerald-500 bg-emerald-500/5 flex items-start gap-3" data-testid="support-preorder-confirm">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sm">Précommande enregistrée !</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              L'équipe AITO.PF vous contactera prochainement pour finaliser.
              En cas d'erreur ou de désistement, tu peux annuler ci-dessous — le compteur sera immédiatement mis à jour.
            </div>
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!submitted?.preorder_id) return;
                  const email = (form.email || "").trim();
                  if (!email) { toast.error("Email requis pour l'annulation."); return; }
                  if (!window.confirm("Confirmer l'annulation de ta précommande ?")) return;
                  try {
                    await api.post(`/support-campaigns/${c.slug}/preorders/${submitted.preorder_id}/cancel`, { email, reason: "Annulation client" });
                    toast.success("Précommande annulée. Merci !");
                    setSubmitted(null);
                    // recharger la campagne pour rafraîchir le compteur
                    const { data } = await api.get(`/support-campaigns/${c.slug}`);
                    setC(data);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Annulation impossible.");
                  }
                }}
                data-testid="support-preorder-self-cancel"
              >
                Annuler ma précommande
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
